import {
  ConnectionStates,
  DeviceGlobalState,
  DevicesRequestStates,
} from "@minervaproject/avsubsystem";
import { Dispatch, UnknownAction } from "redux";

import {
  PreclassTestCategoryType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultType,
  PreclassTestSpecificErrorsType,
} from "../../../shared/types/preclass_check";

import { ComputerTests } from "./computer_tests";
import { ExtensionTest } from "./extension_test";

import { AudioLevelTestStatus } from "components/av/audio_meter";
import { ForumAPI } from "contexts/forum_api_context";
import { HardwareMonitor } from "contexts/hardware_monitor_context";
import { addPreclassCheckResult } from "store/reducers/local";
import { isChrome } from "utils/browser_detection";

type DeviceStateType = {
  micState: DeviceGlobalState;
  cameraState: DeviceGlobalState;
};

const requiredTests = [
  PreclassTestNameType.AUDIO_LEVEL,
  PreclassTestNameType.BROWSER,
  PreclassTestNameType.CONNECTION,
  PreclassTestNameType.FORUM_REQUEST,
  PreclassTestNameType.EXTENSION,
  PreclassTestNameType.MIC_ACCESS_ALLOWED,
  PreclassTestNameType.CPU_CORES,
  PreclassTestNameType.TOTAL_MEMORY,
];

const requiredTestsObserver = [
  PreclassTestNameType.BROWSER,
  PreclassTestNameType.FORUM_REQUEST,
  PreclassTestNameType.CONNECTION,
  PreclassTestNameType.EXTENSION,
  PreclassTestNameType.CPU_CORES,
  PreclassTestNameType.TOTAL_MEMORY,
];

const requiredTestsNoAv = [
  PreclassTestNameType.BROWSER,
  PreclassTestNameType.FORUM_REQUEST,
  PreclassTestNameType.EXTENSION,
  PreclassTestNameType.CPU_CORES,
  PreclassTestNameType.TOTAL_MEMORY,
];

class PreclassTestRunner {
  dispatch: Dispatch<UnknownAction>;

  hardwareMonitor: HardwareMonitor;

  forumAPI: ForumAPI;

  connectionWasFailedBefore: boolean;

  constructor(dispatch: Dispatch<UnknownAction>, hardwareMonitor: HardwareMonitor, userId: string) {
    window.flightLogger.log("[PreclassTestRunner] constructor");
    this.dispatch = dispatch;
    this.hardwareMonitor = hardwareMonitor;
    this.forumAPI = new ForumAPI(dispatch, userId);
    this.connectionWasFailedBefore = false;
  }

  public static getRequiredTests() {
    return requiredTests;
  }

  public static getRequiredTestsObserver() {
    return requiredTestsObserver;
  }

  public static getRequiredTestsNoAv() {
    return requiredTestsNoAv;
  }

  private recordResult(result: PreclassTestResultType) {
    this.dispatch(addPreclassCheckResult(result));
  }

  public async testForumRequest() {
    window.flightLogger.log("[PreclassTestRunner] Enter testForumRequest");
    const response = await this.forumAPI.post("api/v1/current-time", JSON.stringify({}));
    if (response.ok) {
      window.flightLogger.log("[PreclassTestRunner] In testForumRequest: success");
      this.recordResult({
        name: PreclassTestNameType.FORUM_REQUEST,
        outcome: PreclassTestOutcomeType.SUCCESS,
        category: PreclassTestCategoryType.CONNECTION,
      } as PreclassTestResultType);
    } else {
      window.flightLogger.log("[PreclassTestRunner] In testForumRequest: failure");
      this.recordResult({
        name: PreclassTestNameType.FORUM_REQUEST,
        outcome: PreclassTestOutcomeType.FAIL,
        category: PreclassTestCategoryType.CONNECTION,
        error: PreclassTestSpecificErrorsType.FAILED_FORUM_REQUEST,
        data: { message: response.statusText },
      } as PreclassTestResultType);
    }
  }

  public testAudioLevel(alert: AudioLevelTestStatus) {
    window.flightLogger.log(`[PreclassTestRunner] testAudioLevel: ${alert}`);
    let testResult = {
      name: PreclassTestNameType.AUDIO_LEVEL,
      outcome: PreclassTestOutcomeType.IN_PROGRESS,
      category: PreclassTestCategoryType.HARDWARE,
    } as PreclassTestResultType;
    switch (alert) {
      case AudioLevelTestStatus.FAILED:
        testResult = {
          name: PreclassTestNameType.AUDIO_LEVEL,
          outcome: PreclassTestOutcomeType.FAIL,
          category: PreclassTestCategoryType.HARDWARE,
          error: PreclassTestSpecificErrorsType.AUDIO_LEVEL_NOT_DETECTED,
        } as PreclassTestResultType;
        break;
      case AudioLevelTestStatus.SUCCESS:
        testResult = {
          name: PreclassTestNameType.AUDIO_LEVEL,
          outcome: PreclassTestOutcomeType.SUCCESS,
          category: PreclassTestCategoryType.HARDWARE,
        } as PreclassTestResultType;
        break;
      case AudioLevelTestStatus.IN_PROGRESS:
        testResult = {
          name: PreclassTestNameType.AUDIO_LEVEL,
          outcome: PreclassTestOutcomeType.IN_PROGRESS,
          category: PreclassTestCategoryType.HARDWARE,
        } as PreclassTestResultType;
        break;
      default:
        // should not happen
        window.flightLogger.warn(
          `[PreclassTestRunner] Unexpected audio level test status: ${alert}`,
        );
        break;
    }
    this.recordResult(testResult);
  }

  public testDevices(deviceState: DeviceStateType) {
    const micState = deviceState.micState;
    const camState = deviceState.cameraState;
    if (micState.request_state === DevicesRequestStates.requested) {
      const preclassTestResult = {
        name: PreclassTestNameType.MIC_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.IN_PROGRESS,
        category: PreclassTestCategoryType.HARDWARE,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
    if (camState.request_state === DevicesRequestStates.requested) {
      const preclassTestResult = {
        name: PreclassTestNameType.CAMERA_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.IN_PROGRESS,
        category: PreclassTestCategoryType.HARDWARE,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
    if (micState.error) {
      // if the mic has an error, the audio level test is not valid
      const audioLevelTestResult = {
        name: PreclassTestNameType.AUDIO_LEVEL,
        outcome: PreclassTestOutcomeType.FAIL,
        category: PreclassTestCategoryType.HARDWARE,
        error: PreclassTestSpecificErrorsType.AUDIO_LEVEL_NOT_DETECTED,
      } as PreclassTestResultType;

      this.recordResult(audioLevelTestResult);

      const preclassTestResult = {
        name: PreclassTestNameType.MIC_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.FAIL,
        category: PreclassTestCategoryType.HARDWARE,
        error: micState.error,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
    if (camState.error) {
      const preclassTestResult = {
        name: PreclassTestNameType.CAMERA_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.FAIL,
        category: PreclassTestCategoryType.HARDWARE,
        error: camState.error,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
    if (!micState.error && micState.request_state === DevicesRequestStates.granted) {
      const preclassTestResult = {
        name: PreclassTestNameType.MIC_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.SUCCESS,
        category: PreclassTestCategoryType.HARDWARE,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
    if (!camState.error && camState.request_state === DevicesRequestStates.granted) {
      const preclassTestResult = {
        name: PreclassTestNameType.CAMERA_ACCESS_ALLOWED,
        outcome: PreclassTestOutcomeType.SUCCESS,
        category: PreclassTestCategoryType.HARDWARE,
      } as PreclassTestResultType;
      this.recordResult(preclassTestResult);
    }
  }

  public testBrowser() {
    const supportedBrowser = isChrome();
    const preclassTestResult = {
      name: PreclassTestNameType.BROWSER,
      outcome: supportedBrowser ? PreclassTestOutcomeType.SUCCESS : PreclassTestOutcomeType.FAIL,
      category: PreclassTestCategoryType.SOFTWARE,
      error: supportedBrowser ? null : PreclassTestSpecificErrorsType.WRONG_BROWSER,
    } as PreclassTestResultType;
    this.recordResult(preclassTestResult);
  }

  public async testExtension() {
    window.flightLogger.log("[PreclassTestRunner] testExtension");
    const result = await ExtensionTest.testExtension();
    this.recordResult(result);
  }

  public testConnection(connectionState: ConnectionStates) {
    const preclassTestResult = {
      name: PreclassTestNameType.CONNECTION,
      outcome: PreclassTestOutcomeType.IN_PROGRESS,
      category: PreclassTestCategoryType.CONNECTION,
    } as PreclassTestResultType;
    switch (connectionState) {
      case ConnectionStates.connected:
        preclassTestResult.outcome = PreclassTestOutcomeType.SUCCESS;
        this.connectionWasFailedBefore = false;
        break;
      case ConnectionStates.fatalError:
        preclassTestResult.outcome = PreclassTestOutcomeType.FAIL;
        preclassTestResult.error = PreclassTestSpecificErrorsType.AV_CONNECTION_FATAL;
        break;
      case ConnectionStates.reconnecting:
      case ConnectionStates.connecting:
        if (this.connectionWasFailedBefore) {
          preclassTestResult.outcome = PreclassTestOutcomeType.FAIL;
          preclassTestResult.error = PreclassTestSpecificErrorsType.AV_CONNECTION_RETRYING;
        }
        break;
      case ConnectionStates.failed:
        this.connectionWasFailedBefore = true;
        preclassTestResult.outcome = PreclassTestOutcomeType.FAIL;
        preclassTestResult.error = PreclassTestSpecificErrorsType.AV_CONNECTION_RETRYING;
        break;
      default:
        break;
    }
    this.recordResult(preclassTestResult);
  }

  public async testComputer() {
    window.flightLogger.log("[PreclassTestRunner] testComputer");
    const results = await ComputerTests.testComputer(this.hardwareMonitor);
    results.forEach((result) => {
      this.recordResult(result);
    });
  }
}

export { PreclassTestRunner };
